import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ZONES } from "@/lib/zones";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BellRing, Gift, Copy, Award, MapPin, Tag, X, HandHeart } from "lucide-react";
import PushOptInButton from "@/components/PushOptInButton";
import WelcomeOfferDialog from "@/components/WelcomeOfferDialog";

export default function AccountProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || "", last_name: user?.last_name || "",
    city: user?.city || "", postal_code: user?.postal_code || "",
    alerts_optin: !!user?.alerts_optin,
    voisins_alerts: user?.voisins_alerts !== false,
  });
  const [poles, setPoles] = useState(user?.alert_poles || []);
  const [keywords, setKeywords] = useState(user?.alert_keywords || []);
  const [kwInput, setKwInput] = useState("");
  const [voisinCats, setVoisinCats] = useState(user?.voisins_categories || []);
  const [allCats, setAllCats] = useState([]);
  useEffect(() => { api.get("/entre-voisins/meta").then((r) => setAllCats(r.data.categories || [])).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [ref, setRef] = useState(null);
  useEffect(() => { api.get("/me/referral").then((r) => setRef(r.data)).catch(() => setRef(null)); }, []);
  const copyRef = async () => { try { await navigator.clipboard.writeText(ref.link); toast.success("Lien de parrainage copié !"); } catch { toast.message(ref.link); } };

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, alerts_optin: !!user.alerts_optin,
        voisins_alerts: user.voisins_alerts !== false,
        first_name: user.first_name || "", last_name: user.last_name || "",
        city: user.city || "", postal_code: user.postal_code || "" }));
      setPoles(user.alert_poles || []);
      setKeywords(user.alert_keywords || []);
      setVoisinCats(user.voisins_categories || []);
    }
  }, [user]);

  const togglePole = (id) => setPoles((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const addKeyword = (raw) => {
    const parts = String(raw).split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    setKeywords((kw) => {
      const next = [...kw];
      for (const p of parts) if (!next.includes(p) && next.length < 20) next.push(p);
      return next;
    });
    setKwInput("");
  };
  const removeKeyword = (k) => setKeywords((kw) => kw.filter((x) => x !== k));
  const onKwKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addKeyword(kwInput); }
    else if (e.key === "Backspace" && !kwInput && keywords.length) { removeKeyword(keywords[keywords.length - 1]); }
  };

  const save = async () => {
    try {
      const pending = kwInput.trim() ? kwInput.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean) : [];
      const mergedKw = [...keywords];
      for (const p of pending) if (!mergedKw.includes(p) && mergedKw.length < 20) mergedKw.push(p);
      await api.patch("/auth/me", {
        ...form,
        alert_poles: form.alerts_optin ? poles : [],
        alert_keywords: form.alerts_optin ? mergedKw : [],
        voisins_categories: form.voisins_alerts ? voisinCats : [],
      });
      setKeywords(mergedKw); setKwInput("");
      await refresh();
      toast.success("Profil mis à jour");
    } catch { toast.error("Erreur"); }
  };

  const isParticular = user?.role === "particular";
  const allSelected = poles.includes("all");

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <WelcomeOfferDialog />
      <h1 className="font-heading text-2xl font-bold">Mon profil</h1>
      <div className="mt-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom</Label><Input value={form.first_name} onChange={set("first_name")} data-testid="profile-first-name"/></div>
          <div><Label>Nom</Label><Input value={form.last_name} onChange={set("last_name")} data-testid="profile-last-name"/></div>
        </div>
        <div><Label>Email</Label><Input value={user?.email || ""} disabled/></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Ville</Label><Input value={form.city} onChange={set("city")}/></div>
          <div><Label>Code postal</Label><Input value={form.postal_code} onChange={set("postal_code")}/></div>
        </div>

        {isParticular && (
          <>
            <label className="flex items-start gap-3 p-3 mt-2 rounded-xl border border-slate-200 bg-orange-50/40 cursor-pointer hover:border-slate-300"
                   data-testid="profile-alerts-wrapper">
              <input type="checkbox" checked={form.alerts_optin}
                     onChange={(e)=>setForm((f)=>({...f, alerts_optin: e.target.checked}))}
                     className="mt-0.5 w-4 h-4"
                     data-testid="profile-alerts-optin"/>
              <span className="text-sm flex-1">
                <span className="inline-flex items-center gap-2 font-semibold text-slate-900">
                  <BellRing size={14} className="text-[#E05638]"/> M'alerter des nouvelles offres
                </span>
                <span className="block text-xs text-slate-500 mt-1">
                  Recevez un email individuel dès qu'une nouvelle offre correspond à vos critères ci-dessous. Décochez pour arrêter toutes les alertes.
                </span>
              </span>
            </label>

            {form.alerts_optin && (
              <div className="p-4 rounded-xl border border-orange-200 bg-orange-50/30 space-y-4" data-testid="alerts-targeting">
                {/* Pôles suivis */}
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <MapPin size={14} className="text-[#E05638]"/> Pôles à suivre
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    Choisissez les secteurs qui vous intéressent. Si vous ne sélectionnez rien, vous recevez les offres du pôle de votre ville.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={()=>togglePole("all")}
                            data-testid="alert-pole-all"
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${allSelected ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200 hover:border-[#E05638]"}`}>
                      Tout l'Eure-et-Loir
                    </button>
                    {ZONES.map((z) => {
                      const active = !allSelected && poles.includes(z.id);
                      return (
                        <button key={z.id} type="button" onClick={()=>togglePole(z.id)}
                                disabled={allSelected}
                                data-testid={`alert-pole-${z.id}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200 hover:border-[#E05638]"}`}>
                          {z.center}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Mots-clés */}
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Tag size={14} className="text-[#E05638]"/> Mots-clés (facultatif)
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 mb-2">
                    Ex. <span className="italic">restaurant, coiffeur, pizza</span>. On ne vous alerte que si l'offre (titre, description, catégorie ou commerce) contient l'un de vos mots-clés. Laissez vide pour tout recevoir.
                  </p>
                  {keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="alert-keywords-chips">
                      {keywords.map((k) => (
                        <span key={k} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-orange-200 text-[#E05638] text-xs font-semibold" data-testid={`alert-keyword-${k}`}>
                          {k}
                          <button type="button" onClick={()=>removeKeyword(k)} aria-label={`Retirer ${k}`} className="hover:text-rose-600" data-testid={`alert-keyword-remove-${k}`}><X size={12}/></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <Input value={kwInput}
                         onChange={(e)=>setKwInput(e.target.value)}
                         onKeyDown={onKwKeyDown}
                         onBlur={()=>kwInput.trim() && addKeyword(kwInput)}
                         placeholder="Tapez un mot-clé puis Entrée…"
                         data-testid="alert-keyword-input"/>
                </div>
              </div>
            )}

            <div className="p-3 rounded-xl border border-slate-200 bg-emerald-50/40">
              <div className="text-sm font-semibold text-slate-900 mb-1">Notifications push instantanées</div>
              <p className="text-xs text-slate-600 mb-3">
                Soyez alerté·e en temps réel sur votre téléphone dès qu'un nouveau bon plan est disponible dans votre commune.
              </p>
              <PushOptInButton cities={form.city ? [form.city] : []}/>
            </div>
          </>
        )}

        <label className="flex items-start gap-3 p-3 mt-2 rounded-xl border border-slate-200 bg-emerald-50/40 cursor-pointer hover:border-slate-300" data-testid="profile-voisins-wrapper">
          <input type="checkbox" checked={form.voisins_alerts}
                 onChange={(e)=>setForm((f)=>({...f, voisins_alerts: e.target.checked}))}
                 className="mt-0.5 w-4 h-4" data-testid="profile-voisins-optin"/>
          <span className="text-sm flex-1">
            <span className="inline-flex items-center gap-2 font-semibold text-slate-900">
              <HandHeart size={14} className="text-[#E05638]"/> Alertes « Entre Voisins »
            </span>
            <span className="block text-xs text-slate-500 mt-1">
              Recevez un e-mail quand un voisin de votre ville cherche un coup de main. Décochez pour ne plus être alerté.
            </span>
          </span>
        </label>

        {form.voisins_alerts && allCats.length > 0 && (
          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/30" data-testid="voisins-categories">
            <div className="text-sm font-semibold text-slate-900 mb-1">Catégories d'entraide suivies</div>
            <p className="text-xs text-slate-500 mb-2">Sélectionnez les coups de main qui vous intéressent. Laissez vide pour être alerté de toutes les demandes de votre ville.</p>
            <div className="flex flex-wrap gap-2">
              {allCats.map((c) => {
                const active = voisinCats.includes(c);
                return (
                  <button key={c} type="button" data-testid={`voisin-cat-${c}`}
                    onClick={() => setVoisinCats((xs) => xs.includes(c) ? xs.filter((x) => x !== c) : [...xs, c])}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200 hover:border-[#E05638]"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <Button onClick={save} className="btn-shine text-white" data-testid="profile-save-btn">Enregistrer</Button>
      </div>

      {ref && (
        <div className="mt-6 pt-6 border-t border-slate-100" data-testid="referral-card">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Gift size={18} className="text-[#E05638]"/> Parrainez vos proches</h2>
          <p className="text-sm text-slate-600 mt-1">Partagez votre lien : chaque proche qui crée son compte via ce lien devient votre filleul.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="px-3 py-2 rounded-lg bg-slate-100 text-slate-800 font-mono text-sm" data-testid="referral-code">{ref.code}</code>
            <Button variant="outline" onClick={copyRef} data-testid="referral-copy"><Copy size={14} className="mr-1.5"/> Copier mon lien</Button>
            <span className="text-sm text-slate-500" data-testid="referral-count"><b>{ref.count}</b> filleul{ref.count > 1 ? "s" : ""}</span>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E05638]/10 text-[#E05638] text-xs font-bold" data-testid="referral-level">
              <Award size={13}/> {ref.level}
            </span>
            {ref.next_at != null && (
              <span className="text-xs text-slate-500" data-testid="referral-next">Plus que <b>{Math.max(0, ref.next_at - ref.count)}</b> filleul{(ref.next_at - ref.count) > 1 ? "s" : ""} pour le niveau suivant</span>
            )}
          </div>
          {ref.referral_points > 0 && (
            <div className="mt-2 text-sm font-semibold text-emerald-700" data-testid="referral-points">🎁 {ref.referral_points} points parrainage cumulés (+{ref.bonus_per_referral} par filleul)</div>
          )}
        </div>
      )}
    </div>
  );
}
